import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { Modal, FormField } from '../../ui/Modal';
import {
  DayOfWeeks,
  PlacesOfService,
  ProviderRoles,
  ServiceLabels,
  ServiceTypes,
  type DayOfWeek,
  type PlaceOfService,
  type ProviderRole,
  type ServiceType,
} from '@census/shared';

interface ServiceFormState {
  displayName: string;
  durationMin: number;
  allowedRoles: ProviderRole[];
  allowedPlaces: PlaceOfService[];
  telehealthAllowed: boolean;
  defaultStartMin: number | null;
  defaultEndMin: number | null;
  defaultDays: DayOfWeek[];
  groupMaxClients: number | null;
  bundleHints: ServiceType[];
  active: boolean;
}

const DAYS: DayOfWeek[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

export function ServiceForm({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: any | null;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<ServiceFormState>({
    displayName: '',
    durationMin: 60,
    allowedRoles: [],
    allowedPlaces: [],
    telehealthAllowed: false,
    defaultStartMin: null,
    defaultEndMin: null,
    defaultDays: [],
    groupMaxClients: null,
    bundleHints: [],
    active: true,
  });

  useEffect(() => {
    if (!editing) return;
    setForm({
      displayName: editing.displayName ?? '',
      durationMin: editing.durationMin ?? 60,
      allowedRoles: editing.allowedRoles ?? [],
      allowedPlaces: editing.allowedPlaces ?? [],
      telehealthAllowed: editing.telehealthAllowed ?? false,
      defaultStartMin: editing.defaultStartMin ?? null,
      defaultEndMin: editing.defaultEndMin ?? null,
      defaultDays: editing.defaultDays ?? [],
      groupMaxClients: editing.groupMaxClients ?? null,
      bundleHints: editing.bundleHints ?? [],
      active: editing.active !== false,
    });
  }, [editing, open]);

  const save = useMutation({
    mutationFn: () => {
      const body: any = {
        displayName: form.displayName,
        durationMin: form.durationMin,
        allowedRoles: form.allowedRoles,
        allowedPlaces: form.allowedPlaces,
        telehealthAllowed: form.telehealthAllowed,
        defaultDays: form.defaultDays,
        bundleHints: form.bundleHints,
        active: form.active,
      };
      if (form.defaultStartMin != null) body.defaultStartMin = form.defaultStartMin;
      if (form.defaultEndMin != null) body.defaultEndMin = form.defaultEndMin;
      if (form.groupMaxClients != null) body.groupMaxClients = form.groupMaxClients;
      return api.patch(`/services/${editing!.serviceType}`, body).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services'] });
      toast.success('Service updated');
      onClose();
    },
  });

  const toggle = <T extends string>(arr: T[], v: T): T[] =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  if (!editing) return null;
  const isGroup = editing.kind === 'GROUP';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${editing.serviceType}`}
      subtitle={`${isGroup ? 'Group' : 'Individual'} service definition · catalog row`}
      size="xl"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!form.displayName || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Display name" required>
          <input
            className="input"
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
        </FormField>
        <FormField label="Duration (min)" required>
          <input
            type="number"
            className="input"
            min={5}
            max={480}
            value={form.durationMin}
            onChange={(e) => setForm({ ...form, durationMin: Number(e.target.value) })}
          />
        </FormField>
        {isGroup && (
          <>
            <FormField label="Default start time">
              <input
                type="time"
                className="input"
                value={form.defaultStartMin != null ? toHHMM(form.defaultStartMin) : ''}
                onChange={(e) =>
                  setForm({ ...form, defaultStartMin: fromHHMM(e.target.value) })
                }
              />
            </FormField>
            <FormField label="Default end time">
              <input
                type="time"
                className="input"
                value={form.defaultEndMin != null ? toHHMM(form.defaultEndMin) : ''}
                onChange={(e) => setForm({ ...form, defaultEndMin: fromHHMM(e.target.value) })}
              />
            </FormField>
            <FormField label="Group max clients">
              <input
                type="number"
                className="input"
                min={1}
                max={200}
                value={form.groupMaxClients ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    groupMaxClients: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
            </FormField>
          </>
        )}
        <FormField label="Status">
          <label className="mt-1 flex items-center gap-2 rounded border border-line px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <span>{form.active ? 'Active' : 'Inactive (hidden from intake defaults)'}</span>
          </label>
        </FormField>
        <FormField label="Allowed provider roles" hint="Only providers with these roles can deliver this service.">
          <div className="flex flex-wrap gap-1.5">
            {ProviderRoles.map((r) => {
              const on = form.allowedRoles.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => setForm({ ...form, allowedRoles: toggle(form.allowedRoles, r) })}
                  className={
                    on
                      ? 'rounded-md bg-brand-600 px-2.5 py-1 text-2xs font-semibold uppercase text-white'
                      : 'rounded-md bg-surface-muted px-2.5 py-1 text-2xs font-semibold uppercase text-ink-muted hover:bg-line'
                  }
                >
                  {r.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
        </FormField>
        <FormField label="Allowed places of service">
          <div className="flex flex-wrap gap-1.5">
            {PlacesOfService.map((p) => {
              const on = form.allowedPlaces.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setForm({ ...form, allowedPlaces: toggle(form.allowedPlaces, p) })}
                  className={
                    on
                      ? 'rounded-md bg-brand-600 px-2.5 py-1 text-2xs font-semibold uppercase text-white'
                      : 'rounded-md bg-surface-muted px-2.5 py-1 text-2xs font-semibold uppercase text-ink-muted hover:bg-line'
                  }
                >
                  {p === 'HOME_COMMUNITY' ? 'COMMUNITY' : p}
                </button>
              );
            })}
          </div>
        </FormField>
        <FormField label="Telehealth delivery" hint="Whether this service may be delivered via video.">
          <label className="mt-1 flex items-center gap-2 rounded border border-line px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={form.telehealthAllowed}
              onChange={(e) => setForm({ ...form, telehealthAllowed: e.target.checked })}
            />
            <span>{form.telehealthAllowed ? 'Telehealth allowed' : 'In-person only'}</span>
          </label>
        </FormField>
        {isGroup && (
          <FormField label="Default days">
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const on = form.defaultDays.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setForm({ ...form, defaultDays: toggle(form.defaultDays, d) })}
                    className={
                      on
                        ? 'rounded-md bg-brand-600 px-2.5 py-1 text-2xs font-semibold uppercase text-white'
                        : 'rounded-md bg-surface-muted px-2.5 py-1 text-2xs font-semibold uppercase text-ink-muted hover:bg-line'
                    }
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </FormField>
        )}
        <FormField label="Bundles with" hint="Scheduling preference: place this service near these on the same day.">
          <div className="max-h-44 overflow-y-auto rounded-md border border-line p-2">
            <div className="grid grid-cols-2 gap-1">
              {ServiceTypes.filter((s) => s !== editing.serviceType).map((s) => (
                <label
                  key={s}
                  className="flex items-center gap-2 rounded px-1.5 py-1 text-2xs hover:bg-surface-muted"
                >
                  <input
                    type="checkbox"
                    checked={form.bundleHints.includes(s)}
                    onChange={() =>
                      setForm({ ...form, bundleHints: toggle(form.bundleHints, s) })
                    }
                  />
                  <span className="truncate">{ServiceLabels[s]}</span>
                </label>
              ))}
            </div>
          </div>
        </FormField>
      </div>
    </Modal>
  );
}

function toHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function fromHHMM(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}
